import { generateCurrentDomainKey } from '@/utils/license'
import { relationChartConfig, styleByCategory, graphVisColors } from '@/utils/tupuGraphConfig'
import { ensureHttpsUrl } from '@/utils/mediaUrl'
import request from '@/utils/request'

function publicIcon(name) {
  const base = (process.env.BASE_URL || '/').replace(/\/?$/, '/')
  return `${base}graph/${name}.svg`
}

const DEFAULT_NODE_IMAGES = {
  person: publicIcon('person'),
  org: publicIcon('org'),
  event: publicIcon('event'),
  location: publicIcon('location')
}

function firstRawImage(node = {}) {
  const property = node.property || {}
  const candidates = [
    property.icon,
    property.image,
    property.imageUrl,
    property.image_url,
    node.image
  ]
  for (let i = 0; i < candidates.length; i += 1) {
    const value = unwrapRawImage(candidates[i])
    if (value) return value
  }
  const imageSet = property.image_set
  if (Array.isArray(imageSet) && imageSet.length) {
    const value = unwrapRawImage(imageSet[0])
    if (value) return value
  }
  return ''
}

function unwrapRawImage(raw) {
  if (raw == null) return ''
  if (typeof raw === 'object') {
    return unwrapRawImage(raw.url || raw.origin || '')
  }
  let value = String(raw).trim()
  if (!value || value === 'null' || value === '[]' || value === '[""]') return ''
  if (value.startsWith('[')) {
    try {
      const parsed = JSON.parse(value)
      if (Array.isArray(parsed) && parsed.length) {
        return unwrapRawImage(parsed[0])
      }
    } catch (error) {
      return ''
    }
  }
  return value.split(',')[0].trim()
}

/** 把 image/xxx 这类存储路径换成可访问地址 */
async function resolveStorageUrls(paths) {
  const unique = [...new Set(paths.filter((item) => item && !/^https?:\/\//i.test(item)))]
  const urlMap = {}
  if (!unique.length) return urlMap
  const chunkSize = 30
  for (let i = 0; i < unique.length; i += chunkSize) {
    const chunk = unique.slice(i, i + chunkSize)
    try {
      const res = await request({
        url: '/common/getMinioUrl',
        method: 'get',
        params: { url: chunk.join(',') }
      })
      const items = (res && res.items) || (Array.isArray(res) ? res : [])
      items.forEach((item) => {
        if (item && item.origin && item.url) {
          urlMap[item.origin] = ensureHttpsUrl(item.url)
        }
      })
    } catch (error) {
      // 单批失败时回退为类型默认图
    }
  }
  return urlMap
}

/** 实体自有图片优先，否则按类型使用默认图 */
export function resolveNodeImage(node = {}, urlMap = {}) {
  const raw = firstRawImage(node)
  if (raw) {
    if (/^https?:\/\//i.test(raw)) {
      return ensureHttpsUrl(raw)
    }
    if (urlMap[raw]) {
      return urlMap[raw]
    }
  }
  return DEFAULT_NODE_IMAGES[node.category] || DEFAULT_NODE_IMAGES.person
}

export async function buildGraphVisPayload(mapped = {}, options = {}) {
  const nodes = mapped.nodes || []
  const urlMap = options.skipImages
    ? {}
    : await resolveStorageUrls(nodes.map(firstRawImage))
  return toGraphVisPayload(mapped, { ...options, urlMap })
}

export async function applyGraphNodeImages(visgraph, mapped = {}) {
  if (!visgraph || !mapped.nodes || !mapped.nodes.length) return
  const urlMap = await resolveStorageUrls(mapped.nodes.map(firstRawImage))
  const nodes = visgraph.nodes || (typeof visgraph.getNodes === 'function' ? visgraph.getNodes() : []) || []
  nodes.forEach((node) => {
    const source = mapped.nodes.find((item) => String(item.id) === String(node.id))
    if (!source) return
    const image = resolveNodeImage(source, urlMap)
    if (!image || typeof node.setImage !== 'function') return
    node.setImage(image, () => {
      if (typeof visgraph.refreshView === 'function') visgraph.refreshView()
    })
  })
}

/**
 * 将 mapKnowledgeGraph 结果转为 GraphVis 所需 nodes/links
 */
export function toGraphVisPayload(mapped = {}, options = {}) {
  const focusNodeId = mapped.focusNodeId != null ? String(mapped.focusNodeId) : null
  const focusEntityId = mapped.focusEntityId != null ? Number(mapped.focusEntityId) : null

  const nodes = (mapped.nodes || []).map((node) => {
    const isFocus = (focusNodeId && String(node.id) === focusNodeId)
      || (focusEntityId && Number(node.entityId) === focusEntityId)
    const image = resolveNodeImage(node, options.urlMap || {})
    const style = styleByCategory(node.category, isFocus || !!options.forceFocusAll, image)
    return {
      id: String(node.id),
      label: node.name || '未命名',
      type: node.category || '',
      image,
      properties: {
        ...(node.property || {}),
        entity_id: node.entityId,
        identity_name: node.identityName || '',
        name: node.name || '未命名',
        category: node.category || ''
      },
      style: { ...style }
    }
  })

  const linkColor = options.dark ? graphVisColors.linkDark : graphVisColors.link
  const links = (mapped.relations || []).map((relation) => {
    const label = relation.label || relation.type || ''
    const id = relation.id != null && relation.id !== ''
      ? String(relation.id)
      : `e-${relation.source}-${relation.target}`
    return {
      id,
      lid: id,
      label: '',
      type: label,
      source: String(relation.source),
      target: String(relation.target),
      style: {
        lid: id,
        lineType: 'straight',
        color: linkColor,
        strokeColor: linkColor,
        lineWidth: 1.6,
        showArrow: true,
        arrowType: 'triangle',
        showlabel: false
      }
    }
  })

  return { nodes, links }
}

/**
 * @param {HTMLElement} container
 * @param {{ dark?: boolean, config?: object, background?: string }} options
 */
export function createGraphVis(container, options = {}) {
  if (typeof window === 'undefined' || !window.GraphVis) {
    throw new Error('GraphVis 未加载，请检查 index.html 中的 graphvis 脚本')
  }
  if (!container) {
    throw new Error('图谱容器不存在')
  }
  const config = options.config || relationChartConfig({ dark: !!options.dark })
  const background = options.background != null
    ? options.background
    : (options.dark ? '61,43,40' : '245,239,227,0')

  const visgraph = new window.GraphVis({
    container,
    licenseKey: generateCurrentDomainKey(),
    config
  })
  visgraph.setDragHideLine(false)
  visgraph.setSmoothWheelMode(false)
  visgraph.setShowDetailScale(0.1)
  visgraph.setZoomRange([0.2, 8])
  visgraph.setBackGroundColor(background)
  visgraph._container = container
  visgraph._highlightCategory = ''
  visgraph._highlightNodeId = ''
  visgraph._highlightLinkId = ''
  visgraph._pinnedLinkTip = null
  return visgraph
}

/** 从 GraphVis 节点对象还原业务节点字段 */
export function pickBusinessNode(node) {
  if (!node) return null
  const props = node.properties || {}
  const entityId = props.entity_id != null ? Number(props.entity_id) : null
  return {
    id: String(node.id),
    name: props.name || node.label || '未命名',
    entityId: Number.isFinite(entityId) ? entityId : null,
    identityName: props.identity_name || '',
    category: props.category || node.type || ''
  }
}

function getVisNodes(visgraph) {
  if (!visgraph) return []
  return visgraph.nodes || (typeof visgraph.getNodes === 'function' ? visgraph.getNodes() : []) || []
}

function refreshGraphVis(visgraph) {
  if (!visgraph) return
  if (typeof visgraph.refreshView === 'function') {
    visgraph.refreshView()
  } else if (typeof visgraph.refresh === 'function') {
    visgraph.refresh()
  }
}

function hideLinkLabels(visgraph) {
  if (!visgraph) return
  const links = visgraph.links
    || (typeof visgraph.getEdges === 'function' ? visgraph.getEdges() : [])
    || []
  links.forEach((link) => {
    if (!link) return
    link.label = ''
    link.showlabel = false
    if (link.style) {
      link.style.showlabel = false
    }
  })
  refreshGraphVis(visgraph)
}

function findVisNode(visgraph, nodeId) {
  if (!visgraph || nodeId == null || nodeId === '') return null
  return getVisNodes(visgraph).find((node) => String(node.id) === String(nodeId)) || null
}

function collectRelatedNodes(node) {
  if (!node) return []
  const related = [node]
  const seen = new Set([String(node.id)])
  const push = (item) => {
    if (!item || seen.has(String(item.id))) return
    seen.add(String(item.id))
    related.push(item)
  }
  ;(node.outLinks || []).forEach((link) => push(link && link.target))
  ;(node.inLinks || []).forEach((link) => push(link && link.source))
  return related
}

function nodesBoundingBox(nodes) {
  let minX = Infinity
  let minY = Infinity
  let maxX = -Infinity
  let maxY = -Infinity
  ;(nodes || []).forEach((node) => {
    if (!node || !Number.isFinite(node.x) || !Number.isFinite(node.y)) return
    const radius = Number(node.radius) || 28
    minX = Math.min(minX, node.x - radius)
    minY = Math.min(minY, node.y - radius)
    maxX = Math.max(maxX, node.x + radius)
    maxY = Math.max(maxY, node.y + radius)
  })
  if (!Number.isFinite(minX)) return null
  return {
    width: Math.max(maxX - minX, 48),
    height: Math.max(maxY - minY, 48),
    cx: (minX + maxX) / 2,
    cy: (minY + maxY) / 2
  }
}

function getGraphContainerSize(visgraph) {
  const el = visgraph && (visgraph._container || visgraph.container)
  return {
    width: (el && (el.clientWidth || el.offsetWidth)) || 800,
    height: (el && (el.clientHeight || el.offsetHeight)) || 520
  }
}

function computeClusterZoom(visgraph, nodes) {
  const box = nodesBoundingBox(nodes)
  if (!box) return 1.6
  const { width, height } = getGraphContainerSize(visgraph)
  const padding = 140
  const zoom = Math.min(width / (box.width + padding), height / (box.height + padding))
  return Math.max(0.55, Math.min(zoom, 4.8))
}

function setGraphZoom(visgraph, zoom) {
  const safe = Math.max(0.2, Math.min(Number(zoom) || 1, 8))
  if (typeof visgraph.moveCenter === 'function') {
    visgraph.moveCenter(safe)
    return
  }
  if (typeof visgraph.getZoomRatio !== 'function') return
  const stepIn = typeof visgraph.zoomIn === 'function' ? visgraph.zoomIn.bind(visgraph) : null
  const stepOut = typeof visgraph.zoomOut === 'function' ? visgraph.zoomOut.bind(visgraph) : null
  if (!stepIn || !stepOut) return
  let current = Number(visgraph.getZoomRatio()) || 1
  let guard = 0
  while (current < safe * 0.92 && guard < 18) {
    stepOut()
    current = Number(visgraph.getZoomRatio()) || current * 1.2
    guard += 1
  }
  while (current > safe * 1.08 && guard < 28) {
    stepIn()
    current = Number(visgraph.getZoomRatio()) || current / 1.2
    guard += 1
  }
}

function hasPinnedHighlight(visgraph) {
  return !!(visgraph && (
    visgraph._highlightCategory
    || visgraph._highlightNodeId
    || visgraph._highlightLinkId
  ))
}

function nodeDisplayName(node) {
  if (node == null) return '未命名'
  if (typeof node === 'string' || typeof node === 'number') {
    return String(node)
  }
  const props = node.properties || {}
  return props.name || node.label || '未命名'
}

function formatLinkTip(link) {
  if (!link) return ''
  const source = nodeDisplayName(link.source)
  const target = nodeDisplayName(link.target)
  const relation = String(link.type || link.label || '').trim() || '相关'
  return `${source} - ${relation} - ${target}`
}

function getLinkId(link) {
  if (!link) return ''
  if (link.id != null && link.id !== '') return String(link.id)
  if (link.lid != null && link.lid !== '') return String(link.lid)
  const sourceId = link.source && (link.source.id != null ? link.source.id : link.source)
  const targetId = link.target && (link.target.id != null ? link.target.id : link.target)
  return `e-${sourceId}-${targetId}`
}

function pickClientPoint(source) {
  if (!source) return null
  if (Number.isFinite(source.clientX) && Number.isFinite(source.clientY)) {
    return { x: source.clientX, y: source.clientY }
  }
  const touch = source.touches && source.touches[0]
  if (touch && Number.isFinite(touch.clientX) && Number.isFinite(touch.clientY)) {
    return { x: touch.clientX, y: touch.clientY }
  }
  return null
}

function getPointerPoint(visgraph, event) {
  const sources = [
    event,
    event && event.event,
    event && event.originalEvent,
    event && event.srcEvent,
    event && event.nativeEvent,
    typeof window !== 'undefined' ? window.event : null
  ]
  for (let i = 0; i < sources.length; i += 1) {
    const point = pickClientPoint(sources[i])
    if (point) return point
  }
  if (visgraph && visgraph._lastPointer) {
    return visgraph._lastPointer
  }
  return { x: 24, y: 24 }
}

function bindPointerTracker(visgraph) {
  if (!visgraph || visgraph._pointerBound) return
  const container = visgraph._container
  if (!container) return
  visgraph._onPointerMove = (event) => {
    visgraph._lastPointer = { x: event.clientX, y: event.clientY }
    if (visgraph._linkTipEl && visgraph._linkTipEl.style.display !== 'none' && !visgraph._highlightLinkId) {
      placeLinkTip(visgraph, visgraph._lastPointer)
    }
  }
  container.addEventListener('mousemove', visgraph._onPointerMove, true)
  visgraph._pointerBound = true
}

function unbindPointerTracker(visgraph) {
  if (!visgraph || !visgraph._pointerBound) return
  const container = visgraph._container
  if (container && visgraph._onPointerMove) {
    container.removeEventListener('mousemove', visgraph._onPointerMove, true)
  }
  visgraph._onPointerMove = null
  visgraph._pointerBound = false
}

function ensureLinkTip(visgraph) {
  if (visgraph._linkTipEl) {
    return visgraph._linkTipEl
  }
  const el = document.createElement('div')
  el.className = 'graph-link-tip'
  el.style.cssText = [
    'position:fixed',
    'z-index:5000',
    'display:none',
    'max-width:min(420px, calc(100vw - 24px))',
    'padding:10px 16px',
    'border-radius:8px',
    'background:rgba(28,28,32,0.92)',
    'color:#fff',
    'font-size:13px',
    'line-height:1.55',
    'letter-spacing:0.2px',
    'word-break:break-word',
    'pointer-events:none',
    'box-shadow:0 8px 24px rgba(0,0,0,0.28)'
  ].join(';')
  document.body.appendChild(el)
  visgraph._linkTipEl = el
  return el
}

function placeLinkTip(visgraph, point) {
  const el = visgraph && visgraph._linkTipEl
  if (!el || el.style.display === 'none') return
  const width = el.offsetWidth
  const height = el.offsetHeight
  const pad = 8
  const rawX = point && Number.isFinite(point.x) ? point.x : 24
  const rawY = point && Number.isFinite(point.y) ? point.y : 24
  const left = Math.min(Math.max(pad, rawX + 14), Math.max(pad, window.innerWidth - width - pad))
  const top = Math.min(Math.max(pad, rawY + 16), Math.max(pad, window.innerHeight - height - pad))
  el.style.left = `${left}px`
  el.style.top = `${top}px`
}

function showLinkTip(visgraph, text, point) {
  const el = ensureLinkTip(visgraph)
  if (!el || !text) return
  el.textContent = text
  el.style.display = 'block'
  placeLinkTip(visgraph, point)
}

function hideLinkTip(visgraph, force) {
  if (!visgraph) return
  if (visgraph._highlightLinkId && !force) {
    if (visgraph._pinnedLinkTip) {
      showLinkTip(visgraph, visgraph._pinnedLinkTip.text, visgraph._pinnedLinkTip.point)
    }
    return
  }
  if (visgraph._linkTipEl) {
    visgraph._linkTipEl.style.display = 'none'
  }
}

function clearLinkFocus(visgraph) {
  if (!visgraph) return
  visgraph._highlightLinkId = ''
  visgraph._pinnedLinkTip = null
  hideLinkTip(visgraph, true)
}

/** 邻接高亮（参考 utils/index.vue） */
export function bindNeighborHighlight(visgraph) {
  if (!visgraph) return
  visgraph.registEventListener('node', 'mouseOver', (event, node) => {
    if (hasPinnedHighlight(visgraph)) return
    ;(node.outLinks || []).forEach((link) => {
      link.showSelected = true
      if (link.target) link.target.showSelected = true
    })
    ;(node.inLinks || []).forEach((link) => {
      link.showSelected = true
      if (link.source) link.source.showSelected = true
    })
  })
  visgraph.registEventListener('node', 'mouseOut', (event, node) => {
    if (hasPinnedHighlight(visgraph)) return
    ;(node.outLinks || []).forEach((link) => {
      link.showSelected = false
      if (link.target) link.target.showSelected = false
    })
    ;(node.inLinks || []).forEach((link) => {
      link.showSelected = false
      if (link.source) link.source.showSelected = false
    })
  })
}

/** 关系线：hover 只出浮窗；点击高亮该关系及头尾实体 */
export function bindLinkInteractions(visgraph, options = {}) {
  if (!visgraph) return
  bindPointerTracker(visgraph)
  visgraph.registEventListener('link', 'mouseOver', (event, link) => {
    showLinkTip(visgraph, formatLinkTip(link), getPointerPoint(visgraph, event))
  })
  visgraph.registEventListener('link', 'mouseOut', () => {
    hideLinkTip(visgraph, false)
  })
  visgraph.registEventListener('link', 'click', (event, link) => {
    const linkId = getLinkId(link)
    if (visgraph._highlightLinkId && visgraph._highlightLinkId === linkId) {
      clearGraphNeighborFocus(visgraph)
      return
    }
    highlightGraphLink(visgraph, link)
    const point = getPointerPoint(visgraph, event)
    const text = formatLinkTip(link)
    visgraph._pinnedLinkTip = { text, point }
    showLinkTip(visgraph, text, point)
    if (typeof options.onClick === 'function') {
      options.onClick(link)
    }
  })
}

export function highlightGraphLink(visgraph, link) {
  if (!visgraph || !link) return
  visgraph._highlightCategory = ''
  visgraph._highlightNodeId = ''
  const sourceId = String(link.source && (link.source.id != null ? link.source.id : link.source))
  const targetId = String(link.target && (link.target.id != null ? link.target.id : link.target))
  const linkId = getLinkId(link)
  visgraph._highlightLinkId = linkId
  applyAlphaHighlight(
    visgraph,
    (node) => String(node.id) === sourceId || String(node.id) === targetId,
    (item) => getLinkId(item) === linkId,
    true
  )
}

export function fitGraphVis(visgraph) {
  if (!visgraph) return
  try {
    if (typeof visgraph.resize === 'function') {
      visgraph.resize()
    }
    if (typeof visgraph.zoomFit === 'function') {
      visgraph.zoomFit()
    } else if (typeof visgraph.refreshView === 'function') {
      visgraph.refreshView()
    }
  } catch (error) {
    // ignore
  }
}

export function renderGraphVis(visgraph, payload, layoutOptions = {}) {
  if (!visgraph) return
  visgraph._highlightCategory = ''
  visgraph._highlightNodeId = ''
  clearLinkFocus(visgraph)
  if (typeof visgraph.clearAll === 'function') {
    visgraph.clearAll()
  }
  const data = payload || { nodes: [], links: [] }
  if (!data.nodes || !data.nodes.length) {
    return
  }
  visgraph.addGraph(data)
  hideLinkLabels(visgraph)
  const charge = layoutOptions.charge != null ? layoutOptions.charge : -280
  const afterLayout = () => {
    hideLinkLabels(visgraph)
    fitGraphVis(visgraph)
  }
  if (typeof visgraph.excuteLocalLayout === 'function') {
    visgraph.excuteLocalLayout('fastForce', { charge }, afterLayout)
  } else {
    afterLayout()
  }
}

function nodeCategory(node) {
  if (!node) return ''
  return (node.properties && node.properties.category) || node.type || ''
}

function applyAlphaHighlight(visgraph, isNodeActive, isLinkActive, hasFilter) {
  const nodes = getVisNodes(visgraph)
  const links = visgraph.links || []
  nodes.forEach((node) => {
    const matched = !hasFilter || isNodeActive(node)
    node.alpha = matched ? 1 : 0.16
    node.opacity = node.alpha
    node.showSelected = hasFilter && matched
  })
  links.forEach((link) => {
    const matched = !hasFilter || isLinkActive(link)
    link.alpha = matched ? 1 : 0.08
    link.opacity = link.alpha
    link.showSelected = hasFilter && matched
  })
  refreshGraphVis(visgraph)
}

/** 按实体类型高亮；传入空字符串则恢复全部 */
export function highlightGraphCategory(visgraph, category = '') {
  if (!visgraph) return
  visgraph._highlightNodeId = ''
  clearLinkFocus(visgraph)
  visgraph._highlightCategory = category || ''
  const hasFilter = !!category
  applyAlphaHighlight(
    visgraph,
    (node) => nodeCategory(node) === category,
    (link) => nodeCategory(link.source) === category || nodeCategory(link.target) === category,
    hasFilter
  )
}

/** 点击实体：自身与相邻实体高亮，其余变暗（与类型筛选同一套透明度） */
export function highlightGraphNeighbors(visgraph, nodeId = '') {
  if (!visgraph) return
  const target = findVisNode(visgraph, nodeId)
  visgraph._highlightCategory = ''
  clearLinkFocus(visgraph)
  visgraph._highlightNodeId = target ? String(target.id) : ''
  if (!target) {
    applyAlphaHighlight(visgraph, () => true, () => true, false)
    return
  }
  const relatedIds = new Set(collectRelatedNodes(target).map((node) => String(node.id)))
  applyAlphaHighlight(
    visgraph,
    (node) => relatedIds.has(String(node.id)),
    (link) => {
      const sourceId = link.source && String(link.source.id)
      const targetId = link.target && String(link.target.id)
      return relatedIds.has(sourceId)
        && relatedIds.has(targetId)
        && (sourceId === String(target.id) || targetId === String(target.id))
    },
    true
  )
}

/** 点击实体后居中，并把自身与相邻实体放大到可阅读 */
export function focusGraphNeighbors(visgraph, nodeId) {
  if (!visgraph || nodeId == null || nodeId === '') return
  highlightGraphNeighbors(visgraph, nodeId)
  const target = findVisNode(visgraph, nodeId)
  if (!target) return
  const related = collectRelatedNodes(target)
  const zoom = computeClusterZoom(visgraph, related)
  try {
    setGraphZoom(visgraph, zoom)
  } catch (error) {
    // ignore camera fallback errors
  }
  if (typeof visgraph.moveNodeToCenter === 'function') {
    visgraph.moveNodeToCenter(target, 360)
  } else if (typeof visgraph.moveCenter === 'function') {
    visgraph.moveCenter(zoom)
  }
  refreshGraphVis(visgraph)
}

export function clearGraphNeighborFocus(visgraph) {
  if (!visgraph) return false
  const hadFocus = !!(visgraph._highlightNodeId || visgraph._highlightLinkId)
  visgraph._highlightNodeId = ''
  clearLinkFocus(visgraph)
  if (!visgraph._highlightCategory) {
    applyAlphaHighlight(visgraph, () => true, () => true, false)
  }
  return hadFocus
}

export function destroyGraphVis(visgraph) {
  if (!visgraph) return
  try {
    unbindPointerTracker(visgraph)
    if (visgraph._linkTipEl && visgraph._linkTipEl.parentNode) {
      visgraph._linkTipEl.parentNode.removeChild(visgraph._linkTipEl)
    }
    visgraph._linkTipEl = null
    visgraph._pinnedLinkTip = null
    visgraph._lastPointer = null
    if (typeof visgraph.clearAll === 'function') {
      visgraph.clearAll()
    }
    if (typeof visgraph.destroy === 'function') {
      visgraph.destroy()
    }
  } catch (error) {
    // ignore
  }
}
